import { useEffect, useState, Fragment } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItemButton, ListItemIcon, ListItemText,
  Chip, Menu, MenuItem, Button, IconButton, Tooltip, Divider, Link,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import GridViewIcon from '@mui/icons-material/GridView';
import SensorsIcon from '@mui/icons-material/Sensors';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShieldIcon from '@mui/icons-material/Shield';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DnsIcon from '@mui/icons-material/Dns';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { useAuth } from '../contexts/AuthContext';
import { useColorMode } from '../contexts/ColorMode';
import { getContacts } from '../api/featureMap';
import InquiryDialog from './InquiryDialog';

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 56;

// 🔴 hidden: true 는 **메뉴에서만** 감춘다. 라우트는 살려 둔다 —
//   주소를 직접 알고 있거나 북마크한 사람은 그대로 쓸 수 있어야 하고,
//   라우트까지 막으면 기존 링크가 404 가 된다.
//   Features / 매트릭스 뷰: 데이터 정합성 조정이 필요해 일시 숨김 (2026-09-15, PM 판단).
//   조정이 끝나면 hidden 을 지워 다시 노출한다.
const navItems = [
  { to: '/projects',  label: '프로젝트',   icon: <AccountTreeIcon /> },
  { to: '/products',  label: '제품 · SW',  icon: <Inventory2Icon /> },
  { to: '/releases',  label: '릴리즈',     icon: <NewReleasesIcon /> },
  { to: '/features',  label: 'Features',  icon: <HubIcon />, hidden: true },
  { to: '/matrix',    label: '매트릭스 뷰', icon: <GridViewIcon />, hidden: true },
  { to: '/security',  label: '보안 현황',   icon: <ShieldIcon /> },
  { to: '/quality',   label: '테스트 품질', icon: <FactCheckIcon /> },
  { to: '/compliance', label: '규제 준수',  icon: <GavelIcon /> },
  { to: '/compliance/activity', label: '준수 이력', icon: <HistoryIcon /> },
  { to: '/cloud',     label: '클라우드',    icon: <CloudQueueIcon /> },
  { to: '/usage',     label: '사용량',      icon: <QueryStatsIcon /> },
  { to: '/infra',     label: '사내 서버',   icon: <DnsIcon /> },
  { to: '/actions',   label: '예약 실행',   icon: <ScheduleIcon /> },
  { to: '/telemetry', label: '하네스 현황', icon: <SensorsIcon /> },
];

// Ray 플랫폼 패밀리 사이트 — footer 에 표시.
const familySites = [
  { label: 'RAYConnect',    href: 'https://connect.raymedical.com' },
  { label: 'RAYVault',      href: 'https://view.rayplatform.com' },
  { label: 'RAYTool',       href: 'https://tool.rayplatform.com/' },
  { label: 'Ray RA Portal', href: 'https://ra.rayplatform.com' },
  { label: 'RAYFinder',     href: 'https://finder.rayplatform.com/' },
];

export default function Layout() {
  const { userEmail, isAdmin, loading, logout } = useAuth();
  const { mode, toggle } = useColorMode();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [version, setVersion] = useState(null);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  // 좌측 메뉴 접기/펴기 — 선택을 localStorage 에 기억 (테이블 가로폭 확보용).
  const [navOpen, setNavOpen] = useState(() => {
    try { return localStorage.getItem('rederi.navOpen') !== '0'; } catch { return true; }
  });
  const toggleNav = () => setNavOpen((v) => {
    const next = !v;
    try { localStorage.setItem('rederi.navOpen', next ? '1' : '0'); } catch { /* ignore */ }
    return next;
  });
  const navWidth = navOpen ? DRAWER_WIDTH : DRAWER_COLLAPSED;
  const navigate = useNavigate();
  const location = useLocation();

  // REDERI 플랫폼 버전 (로고 옆 표시).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/rederi/version')
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d?.version) setVersion(d.version); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // /ask (REDERI에게 물어보기) 화면에서는 좌측 메뉴를 숨겨 챗에 집중.
  const hideNav = location.pathname === '/ask';

  // 이메일 → 이름 매핑 (contacts). 없으면 이메일 로컬파트로 폴백.
  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    (async () => {
      try {
        const c = await getContacts();
        if (cancelled) return;
        const m = (c.membersFlat || []).find((x) => (x.email || '').toLowerCase() === userEmail.toLowerCase());
        setDisplayName(m?.name || userEmail.split('@')[0]);
      } catch {
        if (!cancelled) setDisplayName(userEmail.split('@')[0]);
      }
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  const nameLabel = (displayName || (userEmail ? userEmail.split('@')[0] : '')) + (isAdmin ? ' · admin' : '');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: 'background.paper', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar>
          {!hideNav && (
            <Tooltip title={navOpen ? '메뉴 접기' : '메뉴 펴기'} arrow>
              <IconButton edge="start" onClick={toggleNav} size="small" sx={{ mr: 1 }} aria-label={navOpen ? '메뉴 접기' : '메뉴 펴기'}>
                {navOpen ? <ChevronLeftIcon /> : <MenuIcon />}
              </IconButton>
            </Tooltip>
          )}
          <Box
            onClick={() => navigate('/')}
            sx={{ flexGrow: 1, display: 'flex', alignItems: 'baseline', cursor: 'pointer', userSelect: 'none' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>REDERI</Typography>
            {version && (
              <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'primary.main', fontWeight: 600, fontFamily: 'JetBrains Mono, monospace' }}>
                {version}
              </Typography>
            )}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', display: { xs: 'none', sm: 'inline' } }}>
              AI 기반 제품·개발 통합 플랫폼
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => navigate('/ask')}
            sx={{ mr: 1, flexShrink: 0 }}
          >
            REDERI에게 물어보기
          </Button>

          <Tooltip title={mode === 'dark' ? '라이트 모드' : '다크 모드'} arrow>
            <IconButton onClick={toggle} size="small" sx={{ mr: 1 }}>
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {!loading && userEmail && (
            <>
              <Chip
                label={nameLabel}
                size="small"
                color={isAdmin ? 'primary' : 'default'}
                variant="outlined"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{ cursor: 'pointer' }}
              />
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                <MenuItem disabled sx={{ opacity: '0.8 !important' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{userEmail}</Typography>
                </MenuItem>
                <MenuItem onClick={() => { setMenuAnchor(null); logout(); }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>로그아웃</ListItemText>
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      {!hideNav && (
        <Drawer
          variant="permanent"
          sx={{
            width: navWidth,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shorter }),
            [`& .MuiDrawer-paper`]: {
              width: navWidth,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shorter }),
            },
          }}
        >
          <Toolbar />
          <Box sx={{ overflow: 'auto', overflowX: 'hidden', mt: 1 }}>
            <List>
              {navItems.filter((item) => !item.hidden).map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {({ isActive }) => (
                    <ListItemButton
                      selected={isActive}
                      sx={{ mx: 1, borderRadius: 1, mb: 0.5, justifyContent: navOpen ? 'initial' : 'center', px: navOpen ? 2 : 1 }}
                    >
                      <Tooltip title={navOpen ? '' : item.label} placement="right" arrow>
                        <ListItemIcon sx={{ minWidth: 0, mr: navOpen ? 2 : 'auto', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                      </Tooltip>
                      {navOpen && <ListItemText primary={item.label} />}
                    </ListItemButton>
                  )}
                </NavLink>
              ))}
              <Divider sx={{ my: 1 }} />
              <ListItemButton
                onClick={() => setInquiryOpen(true)}
                sx={{ mx: 1, borderRadius: 1, mb: 0.5, justifyContent: navOpen ? 'initial' : 'center', px: navOpen ? 2 : 1 }}
              >
                <Tooltip title={navOpen ? '' : '문의하기'} placement="right" arrow>
                  <ListItemIcon sx={{ minWidth: 0, mr: navOpen ? 2 : 'auto', justifyContent: 'center' }}><ContactSupportIcon /></ListItemIcon>
                </Tooltip>
                {navOpen && <ListItemText primary="문의하기" />}
              </ListItemButton>
            </List>
          </Box>
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, maxWidth: '100%', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>

        {!hideNav && (
          <Box
            component="footer"
            sx={{
              mt: 4, pt: 2, borderTop: 1, borderColor: 'divider',
              display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.disabled', mr: 0.5 }}>Family</Typography>
            {familySites.map((s, i) => (
              <Fragment key={s.href}>
                {i > 0 && <Typography component="span" variant="caption" sx={{ color: 'text.disabled' }}>·</Typography>}
                <Link
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="caption"
                  underline="hover"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  {s.label}
                </Link>
              </Fragment>
            ))}
          </Box>
        )}
      </Box>

      <InquiryDialog open={inquiryOpen} onClose={() => setInquiryOpen(false)} />
    </Box>
  );
}
